import React, { useContext } from "react";
import { Grid, Table, TableBody, TableCell, TableRow } from "@mui/material";
import { makeStyles } from "@mui/styles";

import { Body, Button, colorLabel, fontFamily, Label } from "../index";

import {
  addCommaToEvery3Digits,
  changeLoadingType,
  changePackagingType,
  changeVehicleWeight,
  convertDate,
  convertDateWithKorean,
  convertOption,
} from "../../lib";

import { useFormContext } from "react-hook-form";

const useStyles = makeStyles(() => ({
  buttonOrder: {
    width: 203,
    height: 37,
    marginTop: 20,
    fontSize: 13,
    fontFamily,
    fontWeight: 500,
  },
  divider: {
    width: 1,
    height: 20,
    borderLeft: "solid 1px #d9dce6",
    marginLeft: 15,
    marginRight: 14,
    marginBottom: 6,
  },
  driverContainer: {
    display: "flex",
    marginLeft: 15,
    justifyContent: "center",
    alignItems: "center",
  },
  optionContainer: {
    marginTop: 26,
  },
  estimationContainer: {
    marginTop: 20,
  },
  place: {
    marginLeft: 15,
  },
  placeName: {
    fontSize: 14,
    fontWeight: "bold",
  },
  placeDateTime: {
    fontSize: 11,
    color: "rgba(104, 104, 104, 0.6)",
  },
  vehicle: {
    fontSize: 10,
    marginLeft: 5,
  },
  option: {
    fontSize: 11,
  },
  estimation: {
    "& td": {
      border: 0,
      paddingTop: 3,
      paddingBottom: 3,
      paddingLeft: 11,
      paddingRight: 0,
    },
    "& td:first-child": {
      width: 70,
      padding: 0,
      color: colorLabel,
    },
  },
}));

const PlaceName = (props: any) => {
  const classes = useStyles();
  const { name } = props;
  const { watch } = useFormContext();

  return (
    <Body className={classes.placeName}>
      {name === "departure" ? watch("departure.name") : watch("arrival.name")}
    </Body>
  );
};

const DateAndTime = (props: any) => {
  const classes = useStyles();
  const { name } = props;
  const { watch } = useFormContext();
  return (
    <Body classes={{}}>
      {name === "departure" && watch("departure.date") && (
        <Label>{convertDateWithKorean(watch("departure.date"))}</Label>
      )}
      {name === "waypoint0" && watch("waypoint0.date") && (
        <Label>{convertDateWithKorean(watch("waypoint0.date"))}</Label>
      )}
      {name === "waypoint1" && watch("waypoint1.date") && (
        <Label>{convertDateWithKorean(watch("waypoint1.date"))}</Label>
      )}
      {name === "arrival" && watch("arrival.date") && (
        <Label>{convertDateWithKorean(watch("arrival.date"))}</Label>
      )}
    </Body>
  );
};

const PlaceNameForWaypoint = (props: any) => {
  const classes = useStyles();
  const { name } = props;
  const { watch } = useFormContext();

  return (
    <Body className={classes.placeName}>
      {name === "waypoint0" ? watch("waypoint0").name : watch("waypoint1").name}
    </Body>
  );
};

const PackagingType = () => {
  const { watch } = useFormContext();

  return (
    <>
      <TableCell>
        <Label>포장방법</Label>
      </TableCell>
      <TableCell>
        <Body>{changePackagingType(watch("packagingType"))}</Body>
      </TableCell>
    </>
  );
};

const LoadingType = () => {
  const { watch } = useFormContext();

  return (
    <>
      <TableCell>
        <Label>적재방법</Label>
      </TableCell>
      <TableCell>
        <Body>
          {changeLoadingType(watch("loadingType"))} /{" "}
          {changeLoadingType(watch("unloadingType"))}
        </Body>
      </TableCell>
    </>
  );
};

const TotalWeight = () => {
  const { watch } = useFormContext();

  return (
    <>
      {watch("vehicleType") !== "오토바이" &&
        (watch("totalWeight") > 0.001 || watch("totalLength") > 0.01) && (
          <>
            <TableCell>
              <Label>길이/중량</Label>
            </TableCell>
            <TableCell>
              <Body>
                {watch("totalLength") > 0 && `${watch("totalLength")}M`}{" "}
                {watch("totalLength") > 0 && watch("totalWeight") > 0 && ` /`}{" "}
                {watch("totalWeight") > 0 && `${watch("totalWeight")}톤`}
              </Body>
            </TableCell>
          </>
        )}
    </>
  );
};

const VehicleInfo = () => {
  const { watch } = useFormContext();

  return (
    <>
      <TableCell style={{ display: "flex" }}>
        <Label>주문차량</Label>
      </TableCell>
      <TableCell>
        <Body>
          {watch("vehicleType")}/
          {changeVehicleWeight(watch("vehicleType"), watch("vehicleWeight"))}
        </Body>
      </TableCell>
    </>
  );
};

const Options = () => {
  const { watch } = useFormContext();

  return (
    <>
      <TableCell style={{ display: "flex" }}>
        <Label>운송 옵션</Label>
      </TableCell>
      <TableCell>
        <Body>
          {convertOption(
            watch("optionBackDoorOpen"),
            watch("optionForklift"),
            watch("optionShuttle"),
            watch("optionMultiple"),
            watch("orderCount"),
            watch("orderInterval"),
            watch("optionMixedLoading"),
            watch("optionCashOnDelivery")
          )}
        </Body>
      </TableCell>
    </>
  );
};

const Estimate = () => {
  const classes = useStyles();
  const { watch } = useFormContext();

  return (
    <>
      {watch("estimatedDistance") !== "" &&
        watch("estimatedDuration") !== "" && (
          <Table className={classes.estimation}>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Label>예상 거리</Label>
                </TableCell>
                <TableCell>
                  <Body>
                    {watch("estimatedDistance") &&
                      watch("estimatedDistance") + "km"}{" "}
                  </Body>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Label>예상 시간</Label>
                </TableCell>
                <TableCell>
                  <Body>{watch("estimatedDuration")}</Body>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Label>예상 운송료</Label>
                </TableCell>
                <TableCell>
                  <Body>
                    {watch("estimatedCharge")
                      ? addCommaToEvery3Digits(watch("estimatedCharge")) + "원"
                      : "미정"}
                  </Body>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
    </>
  );
};

export default function Summary(props: any) {
  const classes = useStyles();
  const { estimate } = props;
  const { watch } = useFormContext();

  return (
    <Grid container direction={"column"}>
      <Grid item>
        <Grid container direction={"row"}>
          <Grid item>
            <img
              src={
                process.env.PUBLIC_URL +
                "/static/images/summary_place_check.svg"
              }
              alt={"출발지"}
            />
          </Grid>
          <Grid item className={classes.place}>
            <PlaceName name={"departure"} />
            <DateAndTime name={"departure"} />
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <Grid container direction={"row"}>
          <Grid item className={classes.divider}></Grid>
        </Grid>
      </Grid>

      {watch("waypoint0") && (
        <Grid item>
          <Grid container direction={"row"}>
            <Grid item>
              <img
                src={
                  process.env.PUBLIC_URL +
                  "/static/images/summary_place_check.svg"
                }
                alt={"경유지1"}
              />
            </Grid>
            <Grid item className={classes.place}>
              <PlaceNameForWaypoint name={"waypoint0"} />
              <DateAndTime name={"waypoint0"} />
            </Grid>
          </Grid>
          <Grid item className={classes.divider}></Grid>
        </Grid>
      )}

      {watch("waypoint1") && (
        <Grid item>
          <Grid container direction={"row"}>
            <Grid item>
              <img
                src={
                  process.env.PUBLIC_URL +
                  "/static/images/summary_place_check.svg"
                }
                alt={"경유지1"}
              />
            </Grid>
            <Grid item className={classes.place}>
              <PlaceNameForWaypoint name={"waypoint1"} />
              <DateAndTime name={"waypoint1"} />
            </Grid>
          </Grid>
          <Grid item className={classes.divider}></Grid>
        </Grid>
      )}

      <Grid item>
        <Grid container direction={"row"}>
          <Grid item>
            <img
              src={
                process.env.PUBLIC_URL +
                "/static/images/summary_place_check.svg"
              }
              alt={"하차지"}
            />
          </Grid>
          <Grid item className={classes.place}>
            <PlaceName name={"arrival"} />
            <DateAndTime name={"arrival"} />
          </Grid>
        </Grid>
      </Grid>
      <Grid item className={classes.optionContainer}>
        <Table className={classes.estimation}>
          <TableBody>
            <TableRow>
              <VehicleInfo />
            </TableRow>
            <TableRow>
              <Options />
            </TableRow>
            <TableRow>
              <TotalWeight />
            </TableRow>
            <TableRow>
              <PackagingType />
            </TableRow>
            <TableRow>
              <LoadingType />
            </TableRow>
          </TableBody>
        </Table>
      </Grid>
      <Grid item className={classes.estimationContainer}>
        <Estimate />
      </Grid>
      <Grid item>
        <Button type={"submit"} className={classes.buttonOrder}>
          주문하기
        </Button>
      </Grid>
      <Grid item>
        <Button
          className={classes.buttonOrder}
          color={"secondary"}
          onClick={() => {
            window.gtag("event", `운송주문-견적내기 버튼 클릭`, {
              user_id: localStorage.getItem("customerName") || "none",
            });
            estimate();
          }}
        >
          견적내기
        </Button>
      </Grid>
    </Grid>
  );
}
